import { useEffect, useMemo, useRef, useState } from "react";
import {
  InvocationStatusEnum,
  type CompanyShellRow,
  type CompanyLookupResult,
  type MyPayEnvironment,
} from "../../generated/core-api";
import {
  commitOutcomeKind,
  getCommittedRowResults,
  isCompanyShellCommitResult,
  type CompanyShellCommitOutcomeKind,
  type CompanyShellCommitResult,
} from "../types/companyShellCommit";
import { lookupOnboardingCompany } from "./lookupOnboardingCompany";
import { useInvocationStatusPoll } from "./useInvocationStatusPoll";

export const BULK_CREATE_LOOKUP_CONCURRENCY = 8;

export type CommittedRowLookupStatus =
  | "loading"
  | "resolved"
  | "fallback"
  | "error";

export interface BulkCreateCommittedRowLookup {
  rowIndex: number;
  companyCode: string;
  fallbackName: string;
  status: CommittedRowLookupStatus;
  lookup: CompanyLookupResult | null;
  errorMessage: string | null;
}

export interface UseBulkCreateCommitCompanyLookupsArgs {
  agentId: string | null | undefined;
  workflowId: string | null | undefined;
  mypayEnvironment: MyPayEnvironment | null;
  wizardRows: CompanyShellRow[];
  enabled?: boolean;
}

export interface UseBulkCreateCommitCompanyLookupsResult {
  statusQuery: ReturnType<typeof useInvocationStatusPoll>;
  invocationStatus: InvocationStatusEnum | null;
  commitResult: CompanyShellCommitResult | null;
  outcomeKind: CompanyShellCommitOutcomeKind | null;
  committedRowLookups: BulkCreateCommittedRowLookup[];
  isResolvingLookups: boolean;
}

export interface CommittedLookupTarget {
  rowIndex: number;
  companyCode: string;
  fallbackName: string;
}

const EMPTY_LOOKUP_TARGETS: readonly CommittedLookupTarget[] = [];

/** Cache key for resolved lookup batches — includes environment so env switches re-fetch. */
export function buildCommittedLookupCacheKey(
  mypayEnvironment: MyPayEnvironment,
  targets: readonly CommittedLookupTarget[]
): string {
  return `${mypayEnvironment}|${targets
    .map((t) => `${t.rowIndex}:${t.companyCode}`)
    .join("|")}`;
}

/** Build lookup targets for rows the commit workflow marked committed. */
export function buildCommittedLookupTargets(
  commitResult: CompanyShellCommitResult,
  wizardRows: CompanyShellRow[]
): CommittedLookupTarget[] {
  return getCommittedRowResults(commitResult)
    .map((row) => {
      const wizardRow = wizardRows[row.row_index];
      const companyCode = (
        row.company_code?.trim() ||
        wizardRow?.company_code?.trim() ||
        ""
      );
      const fallbackName =
        wizardRow?.company_name?.trim() || companyCode || "—";
      return { rowIndex: row.row_index, companyCode, fallbackName };
    });
}

/** Upload CSV fallbacks when Datapay lookup is unavailable (no environment). */
export function buildUploadFallbackCommittedRowLookups(
  commitResult: CompanyShellCommitResult,
  wizardRows: CompanyShellRow[]
): BulkCreateCommittedRowLookup[] {
  return buildCommittedLookupTargets(commitResult, wizardRows).map((target) => ({
    rowIndex: target.rowIndex,
    companyCode: target.companyCode,
    fallbackName: target.fallbackName,
    status: "fallback",
    lookup: null,
    errorMessage: null,
  }));
}

async function runWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  if (items.length === 0) return [];
  const results = new Array<R>(items.length);
  let nextIndex = 0;

  async function worker(): Promise<void> {
    while (true) {
      const index = nextIndex;
      nextIndex += 1;
      if (index >= items.length) return;
      results[index] = await fn(items[index], index);
    }
  }

  const workerCount = Math.min(limit, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

/**
 * Polls the commit invocation, parses the commit result, and resolves each
 * committed row via onboarding company lookup (#433 step 2).
 */
export function useBulkCreateCommitCompanyLookups({
  agentId,
  workflowId,
  mypayEnvironment,
  wizardRows,
  enabled = true,
}: UseBulkCreateCommitCompanyLookupsArgs): UseBulkCreateCommitCompanyLookupsResult {
  const statusQuery = useInvocationStatusPoll(agentId, workflowId);
  const invocationStatus = statusQuery.data?.status ?? null;

  const commitResult = useMemo(() => {
    if (invocationStatus !== InvocationStatusEnum.COMPLETED) return null;
    const raw = statusQuery.data?.result;
    return isCompanyShellCommitResult(raw) ? raw : null;
  }, [invocationStatus, statusQuery.data?.result]);

  const outcomeKind = commitResult ? commitOutcomeKind(commitResult) : null;

  const lookupTargets = useMemo((): readonly CommittedLookupTarget[] => {
    if (!commitResult || !mypayEnvironment) return EMPTY_LOOKUP_TARGETS;
    return buildCommittedLookupTargets(commitResult, wizardRows);
  }, [commitResult, mypayEnvironment, wizardRows]);

  const lookupTargetKey = useMemo(() => {
    if (!mypayEnvironment) return "";
    return buildCommittedLookupCacheKey(mypayEnvironment, lookupTargets);
  }, [lookupTargets, mypayEnvironment]);

  const uploadFallbackCommittedRowLookups = useMemo(() => {
    if (!commitResult || mypayEnvironment) return [];
    return buildUploadFallbackCommittedRowLookups(commitResult, wizardRows);
  }, [commitResult, mypayEnvironment, wizardRows]);

  const [resolvedCommittedRowLookups, setResolvedCommittedRowLookups] =
    useState<BulkCreateCommittedRowLookup[]>([]);
  const [isResolvingLookups, setIsResolvingLookups] = useState(false);
  const lookupGenerationRef = useRef(0);
  const lastResolvedLookupKeyRef = useRef<string | null>(null);
  const inFlightLookupKeyRef = useRef<string | null>(null);

  const lookupsEnabled =
    enabled &&
    !!mypayEnvironment &&
    lookupTargets.length > 0 &&
    commitResult !== null;

  useEffect(() => {
    if (!lookupsEnabled || !mypayEnvironment) {
      // Invalidate any in-flight batch so a late response cannot repopulate state
      // after lookups are disabled (e.g. environment cleared mid-resolve).
      lookupGenerationRef.current += 1;
      lastResolvedLookupKeyRef.current = null;
      inFlightLookupKeyRef.current = null;
      setResolvedCommittedRowLookups((prev) => (prev.length === 0 ? prev : []));
      setIsResolvingLookups((prev) => (prev ? false : prev));
      return;
    }

    if (lookupTargetKey === lastResolvedLookupKeyRef.current) {
      return;
    }
    if (inFlightLookupKeyRef.current === lookupTargetKey) {
      return;
    }
    inFlightLookupKeyRef.current = lookupTargetKey;

    const generation = lookupGenerationRef.current + 1;
    lookupGenerationRef.current = generation;

    const initial: BulkCreateCommittedRowLookup[] = lookupTargets.map(
      (target) => ({
        rowIndex: target.rowIndex,
        companyCode: target.companyCode,
        fallbackName: target.fallbackName,
        status: "loading",
        lookup: null,
        errorMessage: null,
      })
    );
    setResolvedCommittedRowLookups(initial);
    setIsResolvingLookups(true);

    void (async () => {
      try {
        const resolved = await runWithConcurrency(
          lookupTargets,
          BULK_CREATE_LOOKUP_CONCURRENCY,
          async (target) => {
            const outcome = await lookupOnboardingCompany(
              target.companyCode,
              mypayEnvironment
            );
            return {
              rowIndex: target.rowIndex,
              companyCode: target.companyCode,
              fallbackName: target.fallbackName,
              status:
                outcome.status === "resolved"
                  ? "resolved"
                  : outcome.status === "fallback"
                    ? "fallback"
                    : "error",
              lookup: outcome.lookup,
              errorMessage: outcome.errorMessage,
            } satisfies BulkCreateCommittedRowLookup;
          }
        );

        if (lookupGenerationRef.current !== generation) return;
        setResolvedCommittedRowLookups(resolved);
      } catch {
        // Defensive fallback in case a lookup ever rejects unexpectedly —
        // don't leave the batch (and "Start another bulk upload") stuck.
        if (lookupGenerationRef.current !== generation) return;
        setResolvedCommittedRowLookups((prev) =>
          prev.map((row) =>
            row.status === "loading"
              ? {
                  ...row,
                  status: "error",
                  errorMessage:
                    "Could not confirm in Datapay; retry or check Company List.",
                }
              : row
          )
        );
      } finally {
        if (lookupGenerationRef.current === generation) {
          inFlightLookupKeyRef.current = null;
          lastResolvedLookupKeyRef.current = lookupTargetKey;
          setIsResolvingLookups(false);
        }
      }
    })();
  }, [lookupsEnabled, mypayEnvironment, lookupTargetKey, lookupTargets]);

  const committedRowLookups = mypayEnvironment
    ? resolvedCommittedRowLookups
    : uploadFallbackCommittedRowLookups;

  return {
    statusQuery,
    invocationStatus,
    commitResult,
    outcomeKind,
    committedRowLookups,
    isResolvingLookups,
  };
}
